import { Icon } from '@chakra-ui/react';
import {
  MdLock,
  MdDashboard,
  MdBusiness,
  MdEmail,
  MdEvent,
  MdCardMembership,
  MdAdminPanelSettings,
  MdSupervisedUserCircle,
} from 'react-icons/md';

// Auth Imports
import ForgotPasswordCentered from 'views/auth/forgotPassword/ForgotPasswordCentered';
import LockCentered from 'views/auth/lock/LockCentered';
import SignInCentered from 'views/auth/signIn/SignInCentered';
import SignUpCentered from 'views/auth/signUp/SignUpCentered';
import VerificationCentered from 'views/auth/verification/VerificationCentered';

// Admin Imports
import InternalUsersOverview from 'views/admin/users/internal/overview';
import CreateInternalUser from 'views/admin/users/internal/createUser';
import UpdateInternalUser from 'views/admin/users/internal/updateUser';
import BrilliantUsersOverview from 'views/admin/users/brilliant/overview';
import CreateBrilliantUser from 'views/admin/users/brilliant/createUser';
import UpdateBrilliantUser from 'views/admin/users/brilliant/updateUser';
import UserDetail from 'views/admin/users/brilliant/detail';
import BusinessesOverview from 'views/admin/businesses/overview';
import CreateBusiness from 'views/admin/businesses/createBusiness';
import UpdateBusiness from 'views/admin/businesses/updateBusiness';
import BusinessDetail from 'views/admin/businesses/detail';
import InvitesOverview from 'views/admin/invites/overview';
import EventsOverview from 'views/admin/events/overview';
import EventDetail from 'views/admin/events/detail';
import SubscriptionPlansOverview from 'views/admin/subscriptionPlans/overview';
import SubscriptionPlanDetail from 'views/admin/subscriptionPlans/detail';
import CreateSubscriptionPlan from 'views/admin/subscriptionPlans/createPlan';
import Stats from 'views/admin/stats';
import NonActiveUsers from 'views/admin/users/nonActiveUsers';
import UsersWithSuppliers from 'views/admin/users/brilliant/usersWithSuppliers';

const routes = [
  // --- Stats ---
  {
    isEnabledForSidebar: true,
    name: 'route_names.stats',
    path: '/stats',
    icon: <Icon as={MdDashboard} width="20px" height="20px" color="inherit" />,
    collapse: true,
    items: [
      {
        isEnabledForSidebar: true,
        name: 'route_names.overview',
        layout: '/admin',
        path: '/stats/overview',
        component: <Stats />,
      },
      {
        isEnabledForSidebar: true,
        name: 'route_names.non_active_users',
        layout: '/admin',
        path: '/stats/non_active_users',
        component: (
          <NonActiveUsers
            route="non-active-users"
            filter={{
              daysAgo: 7,
              limit: 6,
              page: 1,
            }}
            title="non_active_users.list_of_non_active_users"
          />
        ),
      },
      {
        isEnabledForSidebar: true,
        name: 'route_names.pending_invites',
        layout: '/admin',
        path: '/stats/pending_invites',
        component: (
          <NonActiveUsers
            route="invites"
            filter={{
              status: 'pending',
              createdAt: new Date().setDate(new Date().getDate() - 7),
              limit: 6,
              page: 1,
            }}
            title="non_active_users.list_of_pending_invites_older_than_7_days"
          />
        ),
      },
    ],
  },
  // --- System Users ---
  {
    isEnabledForSidebar: true,
    name: 'route_names.internal_users',
    path: '/internal_users',
    icon: <Icon as={MdAdminPanelSettings} width="20px" height="20px" color="inherit" />,
    collapse: true,
    items: [
      {
        isEnabledForSidebar: true,
        name: 'route_names.overview',
        layout: '/admin',
        path: '/internal_users/overview',
        component: <InternalUsersOverview />,
      },
      {
        isEnabledForSidebar: true,
        name: 'route_names.create_new_user',
        layout: '/admin',
        path: '/internal_users/new_user',
        component: <CreateInternalUser />,
      },
      {
        isEnabledForSidebar: false,
        name: 'route_names.update_user',
        layout: '/admin',
        path: '/internal_users/update_user',
        component: <UpdateInternalUser />,
      },
    ],
  },
  // --- CRM Users ---
  {
    isEnabledForSidebar: true,
    name: 'route_names.brilliant_users',
    path: '/brilliant_users',
    icon: <Icon as={MdSupervisedUserCircle} width="20px" height="20px" color="inherit" />,
    collapse: true,
    items: [
      {
        isEnabledForSidebar: true,
        name: 'route_names.overview',
        layout: '/admin',
        path: '/brilliant_users/overview',
        component: <BrilliantUsersOverview />,
      },
      {
        isEnabledForSidebar: true,
        name: 'route_names.users_with_suppliers',
        layout: '/admin',
        path: '/brilliant_users/users_with_suppliers',
        component: <UsersWithSuppliers />,
      },
      {
        isEnabledForSidebar: true,
        name: 'route_names.create_new_user',
        layout: '/admin',
        path: '/brilliant_users/new_user',
        component: <CreateBrilliantUser />,
      },
      {
        isEnabledForSidebar: false,
        name: 'route_names.update_user',
        layout: '/admin',
        path: '/brilliant_users/update_user',
        ending: '/:id',
        component: <UpdateBrilliantUser />,
      },
      {
        isEnabledForSidebar: false,
        name: 'route_names.user_detail',
        layout: '/admin',
        path: '/brilliant_users/detail',
        ending: '/:id',
        component: <UserDetail />,
      },
    ],
  },
  // --- Businesses ---
  {
    isEnabledForSidebar: true,
    name: 'route_names.businesses',
    path: '/businesses',
    icon: <Icon as={MdBusiness} width="20px" height="20px" color="inherit" />,
    collapse: true,
    items: [
      {
        isEnabledForSidebar: true,
        name: 'route_names.overview',
        layout: '/admin',
        path: '/businesses/overview',
        component: <BusinessesOverview />,
      },
      {
        isEnabledForSidebar: true,
        name: 'route_names.create_new_business',
        layout: '/admin',
        path: '/businesses/new_business',
        component: <CreateBusiness />,
      },
      {
        isEnabledForSidebar: false,
        name: 'route_names.update_business',
        layout: '/admin',
        path: '/businesses/update_business',
        component: <UpdateBusiness />,
      },
      {
        isEnabledForSidebar: false,
        name: 'route_names.business_detail',
        layout: '/admin',
        path: '/businesses/detail',
        ending: '/:id',
        component: <BusinessDetail />,
      },
    ],
  },
  // --- Invites ---
  {
    isEnabledForSidebar: true,
    name: 'route_names.invites',
    path: '/invites',
    icon: <Icon as={MdEmail} width="20px" height="20px" color="inherit" />,
    collapse: true,
    items: [
      {
        isEnabledForSidebar: true,
        name: 'route_names.overview',
        layout: '/admin',
        path: '/invites/overview',
        component: <InvitesOverview />,
      },
    ],
  },
  // --- Events ---
  {
    isEnabledForSidebar: true,
    name: 'route_names.events',
    path: '/events',
    icon: <Icon as={MdEvent} width="20px" height="20px" color="inherit" />,
    collapse: true,
    items: [
      {
        isEnabledForSidebar: true,
        name: 'route_names.overview',
        layout: '/admin',
        path: '/events/overview',
        component: <EventsOverview />,
      },
      {
        isEnabledForSidebar: false,
        name: 'route_names.event_detail',
        layout: '/admin',
        path: '/events/detail',
        ending: '/:id',
        component: <EventDetail />,
      },
    ],
  },
  // --- Subscription Plans ---
  {
    isEnabledForSidebar: true,
    name: 'route_names.subscription_plans',
    path: '/subscription_plans',
    icon: <Icon as={MdCardMembership} width="20px" height="20px" color="inherit" />,
    collapse: true,
    items: [
      {
        isEnabledForSidebar: true,
        name: 'route_names.overview',
        layout: '/admin',
        path: '/subscription_plans/overview',
        component: <SubscriptionPlansOverview />,
      },
      {
        isEnabledForSidebar: true,
        name: 'route_names.create_plan',
        layout: '/admin',
        path: '/subscription_plans/create',
        component: <CreateSubscriptionPlan />,
      },
      {
        isEnabledForSidebar: false,
        name: 'route_names.plan_detail',
        layout: '/admin',
        path: '/subscription_plans/detail',
        ending: '/:id',
        component: <SubscriptionPlanDetail />,
      },
    ],
  },
  // --- Authentication ---
  {
    name: 'Authentication',
    path: '/auth',
    icon: <Icon as={MdLock} width="20px" height="20px" color="inherit" />,
    collapse: true,
    items: [
      {
        name: 'Sign In',
        path: '/sign-in',
        collapse: true,
        items: [
          {
            name: 'Default',
            layout: '/auth',
            path: '/sign-in',
            component: <SignInCentered />,
          },
        ],
      },
      {
        name: 'Sign Up',
        path: '/sign-up',
        collapse: true,
        items: [
          {
            name: 'Default',
            layout: '/auth',
            path: '/sign-up',
            component: <SignUpCentered />,
          },
        ],
      },
      {
        name: 'Verification',
        path: '/verification',
        collapse: true,
        items: [
          {
            name: 'Default',
            layout: '/auth',
            path: '/verification/default',
            component: <VerificationCentered />,
          },
        ],
      },
      {
        name: 'Lock',
        path: '/lock',
        collapse: true,
        items: [
          {
            name: 'Default',
            layout: '/auth',
            path: '/lock/default',
            component: <LockCentered />,
          },
        ],
      },
      {
        name: 'Forgot Password',
        path: '/forgot-password',
        collapse: true,
        items: [
          {
            name: 'Default',
            layout: '/auth',
            path: '/forgot-password',
            component: <ForgotPasswordCentered />,
          },
        ],
      },
    ],
  },
];

export default routes;
